/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api, businessPath, dateTime, money } from '../lib/api'
import { Badge, PageHeader, State } from '../components/UI'
import { ConfirmDialog } from '../components/Modal'

type Kind = 'expenses' | 'other-income'
type Category = { id: string; name: string; is_active: boolean }

export default function FinancialDetailPage({ kind }: { kind: Kind }) {
  const params = useParams()
  const id = params.expenseId || params.incomeId
  const categoryEndpoint = kind === 'expenses' ? '/expense-categories' : '/other-income-categories'
  const categoryPath = kind === 'expenses' ? '/expenses/categories' : '/other-income/categories'
  const [item, setItem] = useState<Record<string, unknown> | null>(null)
  const [categories, setCategories] = useState<Category[]>([])
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState('')
  const [categoryError, setCategoryError] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirm, setConfirm] = useState(false)

  const load = async () => {
    if (!id) return
    setError('')
    try {
      const record = await api<Record<string, unknown>>(businessPath(`/${kind}/${id}`))
      setItem(record)
      setDescription(String(record.description ?? ''))
      setAmount(String(record.amount ?? ''))
      setCategoryId(String(record.category_id ?? ''))
      setNotes(String(record.notes ?? ''))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to load record.')
    }
  }

  useEffect(() => {
    void load()
    setCategoryError('')
    void api<Category[]>(businessPath(categoryEndpoint))
      .then(setCategories)
      .catch((e) => setCategoryError(e instanceof Error ? e.message : 'Unable to load categories.'))
  }, [id, kind, categoryEndpoint])

  const save = async (cancel = false) => {
    if (!id) return
    setBusy(true)
    setError('')
    try {
      if (cancel) {
        await api(businessPath(`/${kind}/${id}/cancel`), { method: 'POST' })
      } else {
        await api(businessPath(`/${kind}/${id}`), { method: 'PATCH', body: JSON.stringify({ category_id: categoryId, description, amount: Number(amount), notes }) })
      }
      setConfirm(false)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save.')
    } finally {
      setBusy(false)
    }
  }

  if (!item) return <State title="Loading record…">{error}</State>
  const status = String(item.status ?? '—')
  const activeCategories = categories.filter((category) => category.is_active || category.id === categoryId)

  return <section className="aad-page">
    <PageHeader title={kind === 'expenses' ? 'Expense detail' : 'Other income detail'} description={dateTime(item.created_at)} action={<Link className="aad-button aad-button-secondary" to={`/${kind}`}>Back</Link>} />
    {error && <p className="aad-error" role="alert">{error}</p>}
    <div className="detail-grid">
      <form className="aad-card aad-card-padding" onSubmit={(event) => { event.preventDefault(); void save() }}>
        <Badge value={status} />
        <label className="aad-label">Category
          <select className="aad-select" value={categoryId} onChange={(event) => setCategoryId(event.target.value)} disabled={status === 'CANCELLED' || Boolean(categoryError)} required>
            <option value="">Select an active category…</option>
            {activeCategories.map((category) => <option key={category.id} value={category.id}>{category.name}{category.is_active ? '' : ' (inactive)'}</option>)}
          </select>
        </label>
        {categoryError && <p className="aad-error" role="alert">{categoryError}</p>}
        {!categoryError && !activeCategories.length && <p className="aad-muted">No active categories. <Link to={categoryPath}>Manage categories</Link></p>}
        <label className="aad-label">Description<input className="aad-input" value={description} onChange={(event) => setDescription(event.target.value)} disabled={status === 'CANCELLED'} /></label>
        <label className="aad-label">Amount<input className="aad-input" type="number" value={amount} onChange={(event) => setAmount(event.target.value)} disabled={status === 'CANCELLED'} /></label>
        <label className="aad-label">Notes<textarea className="aad-textarea" value={notes} onChange={(event) => setNotes(event.target.value)} disabled={status === 'CANCELLED'} /></label>
        {status !== 'CANCELLED' && <button className="aad-button aad-button-gold" disabled={busy || !categoryId || Boolean(categoryError)}>{busy ? 'Saving…' : 'Save changes'}</button>}
      </form>
      <aside className="aad-card aad-card-padding"><h2 className="aad-section-title">Record</h2><p><strong>{money(item.amount)}</strong></p><p className="aad-muted">{String(item.expense_date ?? item.income_date ?? '')}</p>{status !== 'CANCELLED' && <button className="aad-button aad-button-danger" onClick={() => setConfirm(true)}>Cancel record</button>}</aside>
    </div>
    {confirm && <ConfirmDialog title="Cancel record" close={() => setConfirm(false)} busy={busy} confirm={() => void save(true)}>This cancels the record; it does not delete it.</ConfirmDialog>}
  </section>
}
